import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';

import phrasesType from '../../proptypes/phrases';

const FormContext = React.createContext({ submitting: false, errors: {}, errorMessages: {} });

FormContext.Provider.propTypes = {
  value: PropTypes.exact({
    submitting: PropTypes.bool,
    errors: PropTypes.object,
    errorMessages: PropTypes.object,
    phrases: phrasesType,
  }),
};

const selectErrorMessage = (messages, name, error) => {
  const fieldMessages = messages[camelCase(name)] || {};
  return fieldMessages[camelCase(error)] || startCase(error);
};

const redirectToField = () => {
  const queryParams = new URLSearchParams(window.location.search);
  if (!queryParams.has('redirect_to')) return null;
  return <input type="hidden" name="redirect_to" value={queryParams.get('redirect_to')} />;
};

const Form = ({
  action, method, children, errorMessages, errors: initialErrors,
  inline, negativeMargins, scope, className, phrases, onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState(initialErrors);

  const submitHandler = async (event) => {
    event.preventDefault();

    setSubmitting(true);
    setErrors({});

    const formData = new FormData(event.target);
    const body = Object.fromEntries(formData.entries());
    const token = document.querySelector('[name=csrf-token]')?.content;

    const response = await fetch(
      action,
      {
        method,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': token,
        },
        body: JSON.stringify(scope ? { [scope]: body } : body),
      },
    );

    let jsonResponse;
    try {
      jsonResponse = await response.json();
    } catch {
      // Noop
    }

    switch ((response.status / 100).toFixed(0)) {
      case '2': // 2xx Success
        if (onSuccess) {
          onSuccess(jsonResponse);
        } else if (jsonResponse.redirect_to) {
          window.location.href = jsonResponse.redirect_to;
        }
        return;

      case '4':
      case '5': // 4xx/5xx Errors
        setErrors(scope ? jsonResponse.errors[scope] : jsonResponse.errors);
        break;

      default: // ¯\_(ツ)_/¯
        setErrors({ base: 'unknown_error' });
        break;
    }

    setSubmitting(false);
  };

  return (
    <form className={clsx('ss_form', className, { 'ss_form--submitting': submitting, 'ss_form--inline': inline, 'ss_form--negative-margins': negativeMargins })} onSubmit={submitHandler}>
      <FormContext.Provider
        value={{
          submitting, errors, errorMessages, phrases,
        }}
      >
        {redirectToField()}
        {errors.base && (<div className="ss_form__error_message">{selectErrorMessage(errorMessages, 'base', errors.base)}</div>)}
        {children}
      </FormContext.Provider>
    </form>
  );
};

Form.propTypes = {
  action: PropTypes.string.isRequired,
  method: PropTypes.oneOf(['GET', 'POST', 'PATCH', 'PUT', 'DELETE']),
  children: PropTypes.node.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  errorMessages: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string,
    ]),
  ),
  inline: PropTypes.bool,
  negativeMargins: PropTypes.bool,
  scope: PropTypes.string,
  className: PropTypes.string,
  phrases: phrasesType,
  onSuccess: PropTypes.func,
};

Form.defaultProps = {
  method: 'POST',
  errors: {},
  errorMessages: {},
  inline: false,
  negativeMargins: false,
  scope: undefined,
  className: undefined,
  phrases: {},
  onSuccess: undefined,
};

export {
  Form,
  FormContext,
  selectErrorMessage,
};
